import clsx from 'clsx'
import { useCallback } from 'react'
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd'
import { twMerge } from 'tailwind-merge'

import { reorderArray } from '../../utils'
import { Icon } from '../Icon'
import { Table, TableHead, TableHeader } from '../app/design/Table'
import { ClickableTableRow, TableBody, TableCell, TableRow } from '../app/design/Table'

type Row<T> = T & {
    cells: React.ReactNode[]
    href?: string
}

type Header = {
    label: string
    className?: string
}

type Props<T extends { id: string }> = {
    headers: Header[]
    droppableId: string
    onDragEnd: (source: Row<T>, position: number, list: Row<T>[]) => void
    rows: Row<T>[]
    tableRowClassName?: string
    emptyLayout?: React.ReactNode
}

export function ArrangeableTable<T extends { id: string }>({
    headers,
    droppableId,
    onDragEnd,
    rows,
    tableRowClassName,
    emptyLayout,
}: Props<T>) {
    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) return
            const source = rows[result.source.index]
            const list = reorderArray(rows, result.source.index, result.destination!.index)
            onDragEnd(source, result.destination.index + 1, list) // seqnum is 1-based, so we add 1
        },
        [rows, onDragEnd]
    )

    return (
        <Table>
            <TableHeader>
                <TableRow location="head">
                    {headers.map((header, index) => (
                        <TableHead
                            key={header.label}
                            className={twMerge('font-normal', header.className)}
                        >
                            <div className={index === 0 ? 'pl-10' : ''}>{header.label}</div>
                        </TableHead>
                    ))}
                </TableRow>
            </TableHeader>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={droppableId} isCombineEnabled>
                    {(provided, droppableSnapshot) => (
                        <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                            {rows.map((row, index) => {
                                const RowComponent = Boolean(row.href)
                                    ? ClickableTableRow
                                    : TableRow
                                return (
                                    <Draggable draggableId={row.id} key={row.id} index={index}>
                                        {(provided, snapshot) => {
                                            return (
                                                <RowComponent
                                                    href={row.href ?? ''}
                                                    className={clsx(tableRowClassName, {
                                                        'opacity-50':
                                                            droppableSnapshot.isDraggingOver &&
                                                            !snapshot.isDragging,
                                                        'flex items-center border-0 [&_button]:opacity-0':
                                                            droppableSnapshot.isDraggingOver &&
                                                            snapshot.isDragging,
                                                        'cursor-pointer!': row.href,
                                                    })}
                                                    key={row.id}
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                >
                                                    {row.cells.map((cell, cellIndex) => (
                                                        <TableCell key={cellIndex}>
                                                            {cellIndex === 0 ? (
                                                                <div className="flex w-full items-center gap-x-4">
                                                                    <Icon
                                                                        icon="drag.large"
                                                                        className={clsx(
                                                                            'shrink-0 cursor-grab! text-gray-500'
                                                                        )}
                                                                    />
                                                                    {cell}
                                                                </div>
                                                            ) : (
                                                                cell
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </RowComponent>
                                            )
                                        }}
                                    </Draggable>
                                )
                            })}
                            {provided.placeholder}
                            {rows.length === 0 && emptyLayout}
                        </TableBody>
                    )}
                </Droppable>
            </DragDropContext>
        </Table>
    )
}
